/**
 * Platform admin (dashboard, allowlist, etc.).
 */
export const admin = {
    nav: {
      link: 'Platform Admin',
      dashboard: 'Dashboard',
      platformAdmins: 'Platform Admins',
      authAllowlist: 'Auth Allowlist',
      ageVerification: 'Age/Geofencing',
      spaces: 'Spaces',
      userManagement: 'Users',
      identityManagement: 'Aliases',
      promoCodes: 'Promo Codes',
      announcements: 'Announcements',
      maintenance: 'Maintenance',
    },
    maintenance: {
      title: 'Maintenance',
      subtitle:
        'Data backfills and repairs. Runs proceed in the background in small batches and survive restarts.',
      loadError: 'Failed to load maintenance jobs.',
      actionError: 'That action is no longer available. The job state has been refreshed.',
      empty: 'No maintenance jobs are registered.',
      status: {
        idle: 'Idle',
        running: 'Running',
        paused: 'Paused',
        neverRun: 'Never run',
      },
      outcome: {
        completed: 'Completed',
        failed: 'Failed',
        cancelled: 'Cancelled',
      },
      currentRun: 'Current run',
      lastRun: 'Last run',
      mode: 'Mode',
      modeDryRun: 'Dry run',
      modeLive: 'Live',
      processed: 'Processed',
      changed: 'Changed',
      wouldChange: 'Would change',
      started: 'Started',
      finished: 'Finished',
      error: 'Error',
      dryRun: 'Dry run',
      run: 'Run',
      pause: 'Pause',
      resume: 'Resume',
      cancel: 'Cancel run',
      runConfirm: 'Run "{{title}}" against live data?',
      cancelConfirm: 'Cancel this run? Progress so far is kept; a new run starts from the beginning.',
    },
    dashboard: {
      title: 'Admin dashboard',
      subtitle: 'Platform usage and activity.',
      stats: {
        totalUsers: 'Total users',
        totalIdentities: 'Aliases',
        active15m: 'Active aliases (15 min)',
        active24h: 'Active aliases (24 h)',
        activityLag: 'Activity feed lag',
        activityLagNone: 'Not started',
        activityLagValue: '{{seconds}} s',
      },
      chartTitle: 'Overview',
      chartUsers: 'Users',
      chartIdentities: 'Aliases',
      chartActive15m: 'Active 15m',
      chartActive24h: 'Active 24h',
      loadError: 'Failed to load metrics.',
      geoLabel: 'Your jurisdiction',
      geoCheckedAt: 'Last checked: {{date}}',
    },
    platformAdmins: {
      title: 'Platform admins',
      subtitle: 'Users who can access platform administration.',
      identifierPlaceholder: 'Email or phone (E.164)',
      add: 'Add admin',
      addError: 'Could not add admin. Check the identifier and try again.',
      table: {
        userId: 'User ID',
        displayName: 'Name',
        email: 'Email',
        phone: 'Phone',
        stale: 'Stale',
        actions: 'Actions',
        remove: 'Remove',
      },
      removeConfirm: 'Remove this user from platform admins?',
      loadError: 'Failed to load admins.',
      removeError: 'Failed to remove admin.',
    },
    authAllowlist: {
      title: 'Auth allowlist',
      subtitle: 'Restrict sign-in and sign-up to listed emails and phone numbers when enforcement is on.',
      enforced: 'Enforce allowlist',
      enforcedHint: 'When enabled, only listed identifiers can request or verify OTP.',
      emailList: 'Allowed emails',
      emailHint: 'One email per line, lowercase.',
      phoneList: 'Allowed phone numbers',
      phoneHint: 'One E.164 number per line (e.g. +15551234567).',
      save: 'Save allowlist settings',
      saveError: 'Failed to save settings.',
      loadError: 'Failed to load settings.',
    },
    spaces: {
      title: 'Spaces',
      subtitle: 'Platform controls for Spaces discovery and creation.',
      creationEnabled: 'Allow Space creation',
      creationEnabledHint:
        'When enabled, paid members can create new Spaces. Platform admins can always create, even when this is off.',
      save: 'Save Spaces settings',
      saveError: 'Failed to save settings.',
      loadError: 'Failed to load settings.',
    },
    users: {
      title: 'User Management',
      subtitle: 'Search for and manage user accounts.',
      searchPlaceholder: 'Search by email, phone (+E.164), or account ID',
      searchError: 'Search failed. Please try again.',
      noResults: 'No users found.',
      profileError: 'Failed to load user profile.',
      backToSearch: 'Back to search',
      verified: 'Verified',
      noSessions: 'No active sessions.',
      noAuditEntries: 'No audit log entries.',
      noOverrides: 'No entitlement overrides.',
      bannedBanner: 'This account is permanently banned.',
      suspendedBanner: 'This account is suspended.',
      moderationCategory: 'Category',
      suspendedUntil: 'Suspended until',
      status: {
        active: 'Active',
        suspended: 'Suspended',
        banned: 'Banned',
      },
      avStatus: {
        unverified: 'Unverified',
        pending: 'Pending',
        verified: 'Verified',
        failed: 'Failed',
        expired: 'Expired',
      },
      table: {
        id: 'Account ID',
        contact: 'Contact',
        displayName: 'Display Name',
        created: 'Created',
        status: 'Status',
      },
      sections: {
        basicInfo: 'Basic Information',
        jurisdiction: 'Jurisdiction',
        ageVerification: 'Age Verification',
        billing: 'Billing & Subscription',
        sessions: 'Active Sessions',
        auditLog: 'Activity Log',
      },
      fields: {
        id: 'Account ID',
        email: 'Email',
        phone: 'Phone',
        displayName: 'Display Name',
        createdAt: 'Created',
        lastLogin: 'Last Login',
        jurisdiction: 'Jurisdiction',
        country: 'Country',
        region: 'Region',
        checkedAt: 'Last Checked',
        avStatus: 'Status',
        verifiedAt: 'Verified At',
        failedAt: 'Failed At',
        expirationCount: 'Expiration Count',
        optedIn: 'Opted In',
        subscriptions: 'Subscriptions',
        billingStatus: 'Billing Status',
        isLifetime: 'Lifetime',
        periodEnd: 'Period End',
        entitlements: 'Entitlements',
        overrides: 'Overrides',
        subscriptionOverrides: 'Subscription Overrides',
        device: 'Device',
        ip: 'IP Address',
        lastActivity: 'Last Activity',
        created: 'Created',
        action: 'Action',
        timestamp: 'Timestamp',
        details: 'Details',
      },
      actions: {
        giftSubscription: 'Gift Subscription',
        manageSubscriptions: 'Subscriptions',
        approveAge: 'Approve Age',
        manageEntitlements: 'Entitlements',
        suspend: 'Suspend',
        unsuspend: 'Unsuspend',
        ban: 'Ban',
        unban: 'Unban',
      },
      modals: {
        giftTitle: 'Gift Subscription',
        giftConfirm: 'Gift',
        tier: 'Tier',
        lifetime: 'Lifetime (no expiry)',
        durationMonths: 'Duration (months)',
        approveAgeTitle: 'Approve Age Verification',
        approveAgeConfirm: 'Approve',
        approveAgeDesc: 'This will mark the user as age-verified without going through the provider. Use only for manual verification.',
        entitlementsTitle: 'Manage Entitlements',
        entitlementPlaceholder: 'Entitlement name',
        subscriptionsTitle: 'Manage Subscription Overrides',
        subscriptionsEffective: 'Effective tiers',
        subscriptionsSubtitle: 'Grant or adjust admin subscription overrides. Stripe billing is unchanged.',
        subscriptionsExisting: 'Current overrides',
        subscriptionsAddSection: 'Add override',
        subscriptionsEditSection: 'Edit override',
        subscriptionsAddHint: 'Choose a tier and duration, or mark as lifetime with no expiry.',
        subscriptionsAdd: 'Add override',
        subscriptionsUpdate: 'Save changes',
        subscriptionsCancelEdit: 'Cancel edit',
        subscriptionsNoOverrides: 'No subscription overrides on this account.',
        tierAccess: 'Access',
        tierInsider: 'Insider',
        expiresOn: 'Expires {{date}}',
        suspendTitle: 'Suspend Account',
        suspendConfirm: 'Suspend',
        categoryPreset: 'Category preset',
        categoryNone: 'No preset (custom reason)',
        categoryHint: 'Selecting a preset fills the reason below; you can edit it before confirming.',
        reason: 'Reason',
        durationHours: 'Duration (hours)',
        indefinite: 'Leave empty for indefinite',
        durationHint: 'Leave empty for indefinite suspension.',
        unsuspendTitle: 'Lift Suspension',
        unsuspendConfirm: 'Unsuspend',
        unsuspendDesc: 'This will immediately restore account access. The user will be able to log in again.',
        banTitle: 'Ban Account',
        banConfirm: 'Ban permanently',
        banWarning: 'This action permanently bans the account. All sessions will be revoked immediately.',
        unbanTitle: 'Lift Ban',
        unbanConfirm: 'Unban',
        unbanDesc: 'This will lift the permanent ban and allow the user to log in again.',
        categories: {
          tos_violation: 'Terms of Service violation',
          spam: 'Spam',
          harassment: 'Harassment',
          hate_speech: 'Hate speech',
          violence: 'Violence or threats',
          illegal_content: 'Illegal content',
          csam: 'Child safety (CSAM)',
          impersonation: 'Impersonation',
          fraud: 'Fraud or scams',
          security_abuse: 'Security abuse',
          other: 'Other',
        },
      },
    },
    identities: {
      title: 'Alias Management',
      subtitle: 'Search for and manage Aliases.',
      searchPlaceholder: 'Search by username, display name, or alias ID',
      searchError: 'Search failed. Please try again.',
      noResults: 'No aliases found.',
      profileError: 'Failed to load alias profile.',
      backToSearch: 'Back to search',
      noSessions: 'No active sessions.',
      noOverrides: 'No entitlement overrides.',
      bannedBanner: 'This alias is permanently banned.',
      suspendedBanner: 'This alias is suspended.',
      moderationCategory: 'Category',
      suspendedUntil: 'Suspended until',
      timeRemaining: 'Time remaining',
      status: {
        active: 'Active',
        suspended: 'Suspended',
        banned: 'Banned',
      },
      table: {
        id: 'Alias ID',
        username: 'Username',
        displayName: 'Display Name',
        created: 'Created',
        status: 'Status',
      },
      sections: {
        basicInfo: 'Basic Information',
        platformAccess: 'Platform Access',
        stats: 'Activity Stats',
        sessions: 'Active Sessions',
        reports: 'Content Reports',
        entitlements: 'Entitlements',
      },
      fields: {
        id: 'Alias ID',
        username: 'Username',
        displayName: 'Display Name',
        bio: 'Bio',
        createdAt: 'Created',
        lastActive: 'Last Active',
        messagesSent: 'Messages Sent',
        conversationsJoined: 'Conversations Joined',
        friends: 'Friends',
        achievementsEarned: 'Achievements Earned',
        device: 'Device',
        lastActivity: 'Last Activity',
        created: 'Created',
        overrides: 'Overrides',
      },
      reports: {
        against: 'Reports Against',
        by: 'Reports By',
        noReports: 'No reports found.',
        type: 'Type',
        category: 'Category',
        status: 'Status',
        date: 'Date',
      },
      roles: {
        title: 'Roles',
        admin: 'Admin',
        moderator: 'Moderator',
        support_agent: 'Support Agent',
        grant: 'Grant Role',
        revoke: 'Revoke',
        revokeConfirm: 'Revoke the {{role}} role from this alias?',
        noRoles: 'No platform roles assigned.',
        grantError: 'Failed to grant role.',
        revokeError: 'Failed to revoke role.',
        lastAdminError: 'Cannot revoke -- this is the last admin.',
        selectRole: 'Select role',
      },
      attributes: {
        title: 'Direct Permission Grants',
        grant: 'Grant Attribute',
        revoke: 'Revoke',
        revokeConfirm: 'Revoke the {{attribute}} permission from this alias?',
        noAttributes: 'No direct permission grants.',
        grantError: 'Failed to grant attribute.',
        revokeError: 'Failed to revoke attribute.',
        selectAttribute: 'Select permission',
      },
      actions: {
        manageEntitlements: 'Entitlements',
        suspend: 'Suspend',
        unsuspend: 'Unsuspend',
        ban: 'Ban',
        unban: 'Unban',
      },
      modals: {
        entitlementsTitle: 'Manage Entitlements',
        entitlementPlaceholder: 'Entitlement name',
        categoryPreset: 'Category preset',
        categoryNone: 'No preset (custom reason)',
        categoryHint: 'Selecting a preset fills the reason below; you can edit it before confirming.',
        reason: 'Reason',
        durationHours: 'Duration (hours)',
        indefinite: 'Leave empty for indefinite',
        durationHint: 'Leave empty for indefinite suspension.',
        suspendTitle: 'Suspend Alias',
        suspendConfirm: 'Suspend',
        unsuspendTitle: 'Lift Suspension',
        unsuspendConfirm: 'Unsuspend',
        unsuspendDesc: 'This will immediately restore alias access. Active sessions will need to be re-established.',
        banTitle: 'Ban Alias',
        banConfirm: 'Ban permanently',
        banWarning: 'This action permanently bans the alias. All sessions will be revoked immediately.',
        unbanTitle: 'Lift Ban',
        unbanConfirm: 'Unban',
        unbanDesc: 'This will lift the ban and allow the alias to be used again.',
        categories: {
          tos_violation: 'Terms of Service violation',
          spam: 'Spam',
          harassment: 'Harassment',
          hate_speech: 'Hate speech',
          violence: 'Violence or threats',
          illegal_content: 'Illegal content',
          csam: 'Child safety (CSAM)',
          impersonation: 'Impersonation',
          fraud: 'Fraud or scams',
          security_abuse: 'Security abuse',
          other: 'Other',
        },
      },
    },
    promoCodes: {
      title: 'Promotional codes',
      subtitle: 'Create and manage codes that grant subscriptions or entitlements.',
      empty: 'No promotional codes yet.',
      loadError: 'Failed to load promotional codes.',
      saveError: 'Failed to save promotional code.',
      createSuccess: 'Promotional code created.',
      updateSuccess: 'Promotional code updated.',
      deleteError: 'Failed to delete promotional code.',
      deleteConfirm: 'Delete promotional code "{{shortcode}}"? This cannot be undone.',
      showingCount: 'Showing {{start}}-{{end}} of {{total}} codes.',
      firstPage: 'First',
      prevPage: 'Previous',
      nextPage: 'Next',
      lastPage: 'Last',
      addTitle: 'Add promotional code',
      editTitle: 'Edit {{shortcode}}',
      add: 'Create code',
      update: 'Save changes',
      edit: 'Edit',
      delete: 'Delete',
      cancelEdit: 'Cancel edit',
      viewRedemptions: 'Redemptions',
      unlimited: 'Unlimited',
      grantsNone: 'None',
      grantSubscriptionSummary: '{{tier}} for {{months}} months',
      grantLifetimeSummary: '{{tier}} (Lifetime)',
      validityAlways: 'Always valid',
      noStart: 'No start',
      noEnd: 'No end',
      jurisdictionsAll: 'All',
      jurisdictionCount: '{{count}} restricted',
      tierAccess: 'Access',
      tierInsider: 'Insider',
      audienceAll: 'All users',
      audienceFirstTime: 'First-time subscribers only',
      audienceUnsubscribed: 'Not currently subscribed',
      table: {
        shortcode: 'Code',
        description: 'Description',
        grants: 'Grants',
        uses: 'Uses',
        validity: 'Validity',
        jurisdictions: 'Jurisdictions',
        audience: 'Audience',
        actions: 'Actions',
      },
      form: {
        shortcode: 'Shortcode',
        description: 'Description',
        grantSubscription: 'Grant subscription tier',
        tier: 'Tier',
        lifetime: 'Lifetime (no expiry)',
        durationMonths: 'Duration (months)',
        entitlements: 'Entitlements',
        requiredCodes: 'Required codes',
        incompatibleCodes: 'Incompatible codes',
        unlimitedUses: 'Unlimited uses',
        maxUses: 'Maximum uses',
        jurisdictions: 'Jurisdictions (comma-separated, empty = all)',
        validFrom: 'Valid from (optional)',
        validTo: 'Valid until (optional)',
        audience: 'Audience',
      },
      validation: {
        shortcodeRequired: 'Enter a shortcode.',
        shortcodeInvalid: 'Shortcode must be 1–32 characters using letters, numbers, and hyphens only.',
        durationInvalid: 'Subscription duration must be between 1 and 120 months.',
        maxUsesInvalid: 'Maximum uses must be at least 1.',
        validityRangeInvalid: 'Valid from must be before valid until.',
      },
      redemptionsTitle: 'Redemptions for {{shortcode}}',
      redemptionsSubtitle: 'Accounts that have redeemed this promotional code.',
      redemptionsEmpty: 'No redemptions yet.',
      redemptionsLoadError: 'Failed to load redemptions.',
      redemptions: {
        userId: 'User ID',
        redeemedAt: 'Redeemed at',
        grantsApplied: 'Grants applied',
        subscriptionGrant: '{{tier}} until {{date}}',
        subscriptionGrantLifetime: '{{tier}} (lifetime)',
      },
    },
    announcements: {
      title: 'Site announcements',
      subtitle: 'Create and manage sitewide announcements shown to all users.',
      empty: 'No announcements yet.',
      loadError: 'Failed to load announcements.',
      saveError: 'Failed to save announcement.',
      ctaPairError: 'Both CTA label and CTA link must be provided together, or leave both empty.',
      dateRangeError: '"Show after" must be earlier than "Show until".',
      activeToggleLabel: 'Toggle active state for {{name}}',
      deleteConfirm: 'Delete this announcement? This cannot be undone.',
      addTitle: 'Create announcement',
      editTitle: 'Edit announcement',
      add: 'Create announcement',
      update: 'Save changes',
      edit: 'Edit',
      delete: 'Delete',
      cancelEdit: 'Cancel edit',
      highPriority: 'High priority',
      always: 'Always',
      table: {
        active: 'Active',
        message: 'Announcement',
        priority: 'Priority',
        window: 'Time window',
        actions: 'Actions',
      },
      form: {
        message: 'Message',
        title: 'Title (optional)',
        ctaLabel: 'CTA label (optional)',
        ctaUrl: 'CTA link (optional)',
        showAfter: 'Show after (optional)',
        showUntil: 'Show until (optional)',
        highPriority: 'High priority (show in sidebar)',
        dismissable: 'Dismissable',
      },
    },
} as const;
